import React from 'react';
import { BookOpen, PenTool, Library, MessageCircle, FileSearch, Sparkles, FolderOpen, Layers } from 'lucide-react';

const NotebookSection = () => {
  const features = [
    {
      icon: Library,
      title: "Kişisel Kütüphane",
      description: "Tüm dokümanlarınızı tek bir yerde toplayın. PDF, Word, metin dosyaları - hepsi organize ve aranabilir."
    },
    {
      icon: MessageCircle,
      title: "Dokümanlarla Sohbet",
      description: "Dokümanlarınıza doğal dilde soru sorun. AI, kaynaklarınızdan yanıt üretir ve referans gösterir."
    },
    {
      icon: PenTool,
      title: "Akıllı Yazım Asistanı",
      description: "İçerik oluşturun, özetler yazın, metinlerinizi geliştirin. AI destekli yazım ortamı."
    },
    {
      icon: FileSearch,
      title: "Derin Arama",
      description: "Sadece başlıklarda değil, içeriklerde de arama yapın. Semantik arama ile ilgili bilgileri bulun."
    },
    {
      icon: Sparkles,
      title: "Otomatik Özetleme",
      description: "Uzun dokümanları anında özetleyin. Anahtar noktaları çıkarın, zaman kazanın."
    },
    {
      icon: Layers,
      title: "Çoklu Kaynak Analizi",
      description: "Birden fazla dokümanı aynı anda analiz edin. Karşılaştırın, sentezleyin, bağlantılar kurun."
    }
  ];

  return (
    <section id="notebook" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-1.5 rounded-full bg-amber-100 text-amber-700 text-sm font-semibold mb-4">
            Kişisel Çalışma Alanı
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-slate-800 mb-6">
            Kişisel Kütüphaneniz,
            <span className="text-blue-600"> AI Gücüyle</span>
          </h2>
          <p className="max-w-3xl mx-auto text-lg text-slate-600">
            NotebookLM benzeri kişisel doküman yönetimi ve yazım ortamı. 
            Dokümanlarınızı yükleyin, sorular sorun, içerik üretin.
          </p>
        </div>

        {/* Main Feature Showcase */}
        <div className="grid lg:grid-cols-2 gap-8 mb-16">
          {/* Left - Visual */}
          <div className="bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 rounded-3xl p-8 md:p-10 relative overflow-hidden">
            {/* Background Elements */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/20 rounded-full blur-3xl" />
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-violet-500/20 rounded-full blur-3xl" />
            
            <div className="relative">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center">
                  <BookOpen className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">CAPSNA Notebook</h3>
                  <p className="text-blue-300 text-sm">Kişisel AI Asistanınız</p>
                </div>
              </div>

              {/* Chat-like Interface Preview */}
              <div className="space-y-4">
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/10">
                  <p className="text-blue-200 text-sm mb-2">Soru:</p>
                  <p className="text-white">"Bu dokümandaki ana bulgular nelerdir?"</p>
                </div>
                
                <div className="bg-blue-600/30 backdrop-blur-sm rounded-xl p-4 border border-blue-400/30">
                  <p className="text-blue-200 text-sm mb-2">CAPSNA:</p>
                  <p className="text-white text-sm leading-relaxed">
                    Doküman analiz edildi. 3 ana bulgu tespit edildi:
                  </p>
                  <ul className="mt-2 space-y-1 text-blue-100 text-sm">
                    <li>• Verimlilik %40 artmıştır</li>
                    <li>• Maliyet tasarrufu sağlanmıştır</li>
                    <li>• Kullanıcı memnuniyeti yükselmiştir</li>
                  </ul>
                  <p className="text-blue-300 text-xs mt-3">📄 Kaynak: Rapor.pdf, Sayfa 12-15</p>
                </div>
              </div>

              {/* Feature Pills */}
              <div className="flex flex-wrap gap-2 mt-6">
                <span className="px-3 py-1 rounded-full bg-white/10 text-white text-xs">Doğal Dil</span>
                <span className="px-3 py-1 rounded-full bg-white/10 text-white text-xs">Kaynak Gösterimi</span>
                <span className="px-3 py-1 rounded-full bg-white/10 text-white text-xs">TR/EN</span>
              </div>
            </div>
          </div>

          {/* Right - Features List */}
          <div className="grid sm:grid-cols-2 gap-4">
            {features.map((feature, idx) => (
              <div
                key={idx}
                className="group p-5 rounded-xl bg-slate-50 hover:bg-white hover:shadow-lg border border-transparent hover:border-slate-200 transition-all duration-300"
              >
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center mb-3 group-hover:bg-blue-600 transition-colors">
                  <feature.icon className="w-5 h-5 text-blue-600 group-hover:text-white transition-colors" />
                </div>
                <h4 className="font-semibold text-slate-800 mb-2">{feature.title}</h4>
                <p className="text-sm text-slate-600 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="text-center bg-gradient-to-r from-amber-50 to-orange-50 rounded-2xl p-8 border border-amber-200">
          <FolderOpen className="w-12 h-12 text-amber-600 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-slate-800 mb-2">
            Tüm Dokümanlarınız Tek Yerde
          </h3>
          <p className="text-slate-600 max-w-2xl mx-auto">
            PDF, Word, metin dosyaları, web sayfaları, YouTube videoları... 
            Tüm kaynaklarınızı CAPSNA'ya ekleyin, AI ile analiz edin ve içgörüler elde edin.
          </p>
        </div>
      </div>
    </section>
  );
};

export default NotebookSection;
